// The RBP links read: `GET /api/v1/catalogue/products/:sku/rbp-links?entry=`. The wire shape is a
// LOCK-STEP COPY of search's `RbpLinksDto` (libs/modules/search-and-discovery contracts/dtos/
// rbp-links.dto.ts). The product code travels as a path segment only, never in the query string.

import { API_PREFIX, apiFetch } from './shoplink-api';
import { withEntry } from './catalogue-entry';
import type { SearchResultProduct } from './search.client';

/** A product's RBP links: its children the selected catalogue lists, as browse lists them. */
export interface RbpLinks {
  /** The asked product, for the heading; null when it is not an RBP parent here. */
  readonly parent: { readonly sku: string; readonly name: string } | null;
  /** In product-code order; an off-sale child is marked `notAvailable`. */
  readonly products: readonly SearchResultProduct[];
}

/** The backend path for a product's RBP links. */
export function rbpLinksPath(sku: string): string {
  return `${API_PREFIX}/catalogue/products/${encodeURIComponent(sku)}/rbp-links`;
}

/**
 * The RBP links of `sku` in the selected ordering `entry`'s catalogue. A 404 means the caller sees
 * no RBP feature there, and resolves to `null` ("no links"); any other failure throws, so a page
 * can show its error state.
 */
export async function fetchRbpLinks(entry: string, sku: string): Promise<RbpLinks | null> {
  const res = await apiFetch(withEntry(rbpLinksPath(sku), entry));
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load the RBP links (${res.status})`);
  return (await res.json()) as RbpLinks;
}
